import React, { useState, useEffect, useCallback } from 'react';
import './EmployeeAvailability.css';
import employeeAuthService from '../services/employee-auth.service';
import apiService from '../services/api.service';
import LoadingIndicator from './LoadingIndicator';
import toast from '../services/toast.service';

// Time conversion utilities
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const formatDate = (dateString) => {
  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('tr-TR', options);
};

const weekDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const EmployeeAvailability = () => {
  const [weeklyShiftTypes, setWeeklyShiftTypes] = useState({});
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()).toISOString().split('T')[0]);
  const [availabilities, setAvailabilities] = useState({});
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingChanges, setProcessingChanges] = useState(new Set()); // Track individual checkbox operations
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(employeeAuthService.getUser());

  // Get Monday of current week
  function getMonday(d) {
    d = new Date(d);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(d.setDate(diff));
  }
  
  // Generate dates for the current week
  const getWeekDates = useCallback(() => {
    const dates = [];
    const monday = new Date(currentWeekStart);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        dayName: weekDayNames[i],
        displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'numeric' })
      });
    }
    
    return dates;
  }, [currentWeekStart]);
  
  // Get previous week
  const getPreviousWeek = () => {
    const monday = new Date(currentWeekStart);
    monday.setDate(monday.getDate() - 7);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };
  
  // Get next week
  const getNextWeek = () => {
    const monday = new Date(currentWeekStart);
    monday.setDate(monday.getDate() + 7);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };
  
  // Go to current week
  const goToCurrentWeek = () => {
    setCurrentWeekStart(getMonday(new Date()).toISOString().split('T')[0]);
  };

  // Show success message temporarily
  const showSuccessMessage = useCallback((message) => {
    toast.success(message);
  }, []);

  // Show error message
  const showErrorMessage = useCallback((message) => {
    toast.error(message);
    // Also keep it in state for critical errors that should persist
    setError(message);
    // Clear error after 10 seconds
    setTimeout(() => {
      setError(null);
    }, 10000);
  }, []);

  // Initialize component with data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Check for user
        const user = employeeAuthService.getUser();
        if (!user) {
          const errorMsg = 'Kullanıcı oturumu bulunamadı. Lütfen giriş yapın.';
          setError(errorMsg);
          toast.error(errorMsg);
          setLoading(false);
          return;
        }
        setCurrentUser(user);
        
        // Load data
        await fetchShiftTypes();
        await fetchAvailabilities();
      } catch (err) {
        console.error('Error initializing component:', err);
        const errorMsg = 'Veriler yüklenirken bir hata oluştu: ' + 
                (err.response?.data?.message || err.message || 'Bilinmeyen hata');
        showErrorMessage(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Refresh data when week changes
  useEffect(() => {
    if (currentUser && !loading) {
      fetchAvailabilities();
    }
  }, [currentWeekStart, currentUser, loading]);

  // Fetch shift types
  const fetchShiftTypes = async () => {
    try {
      const response = await apiService.getShiftTypes();
      
      if (!response || !response.data) {
        throw new Error('Vardiya türleri için geçersiz yanıt');
      }
      
      setShiftTypes(response.data);
      
      // Initialize weekly shift types
      const dates = getWeekDates();
      const initialShiftTypes = {};
      
      dates.forEach(day => {
        initialShiftTypes[day.date] = response.data;
      });
      
      setWeeklyShiftTypes(initialShiftTypes);
      return true;
    } catch (err) {
      console.error('Error fetching shift types:', err);
      throw err;
    }
  };

  // Fetch employee's availabilities
  const fetchAvailabilities = async () => {
    try {
      if (!currentUser?.id) {
        throw new Error('Kullanıcı ID bulunamadı');
      }
      
      const response = await apiService.getEmployeeAvailabilities(
        currentUser.id, 
        currentWeekStart
      );
      
      // Convert the array to an object keyed by date and shiftTypeId
      const availabilitiesMap = {};
      
      if (response?.data) {
        response.data.forEach(avail => {
          if (!availabilitiesMap[avail.date]) {
            availabilitiesMap[avail.date] = {};
          }
          availabilitiesMap[avail.date][avail.shiftTypeId] = avail;
        });
      }
      
      setAvailabilities(availabilitiesMap);
      setError(null);
      return true;
    } catch (err) {
      console.error('Error fetching availabilities:', err);
      const errorMsg = 'Uygunluk bilgileri yüklenirken bir hata oluştu: ' + 
               (err.response?.data?.message || err.message || 'Bilinmeyen hata');
      showErrorMessage(errorMsg);
      return false;
    }
  };

  // Handle availability toggle - optimized to avoid full page refresh
  const toggleAvailability = async (date, shiftTypeId) => {
    if (!currentUser?.id) {
      showErrorMessage('Oturum bilgileriniz bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }
    
    const changeKey = `${date}-${shiftTypeId}`;
    
    // Prevent multiple simultaneous requests for the same checkbox
    if (processingChanges.has(changeKey)) {
      return;
    }
    
    // Add to processing set
    setProcessingChanges(prev => new Set([...prev, changeKey]));
    
    try {
      const isCurrentlyAvailable = availabilities[date]?.[shiftTypeId] !== undefined;
      
      if (isCurrentlyAvailable) {
        // Delete availability
        await apiService.deleteAvailability(availabilities[date][shiftTypeId].id);
        
        // Update local state immediately for better UX
        setAvailabilities(prev => {
          const updated = { ...prev };
          if (updated[date]) {
            delete updated[date][shiftTypeId];
            if (Object.keys(updated[date]).length === 0) {
              delete updated[date];
            }
          }
          return updated;
        });
        
        showSuccessMessage('Uygunluk durumu başarıyla kaldırıldı');
      } else {
        // Create availability
        const response = await apiService.createAvailability({
          employeeId: currentUser.id,
          date,
          shiftTypeId,
          status: 'pending' // Initial status
        });
        
        // Update local state immediately for better UX
        setAvailabilities(prev => {
          const updated = { ...prev };
          if (!updated[date]) {
            updated[date] = {};
          }
          updated[date][shiftTypeId] = response.data;
          return updated;
        });
        
        showSuccessMessage('Uygunluk durumu başarıyla kaydedildi');
      }
      
    } catch (err) {
      console.error('Error toggling availability:', err);
      const errorMsg = 'Uygunluk durumu güncellenirken bir hata oluştu: ' + 
               (err.response?.data?.message || err.message || 'Bilinmeyen hata');
      showErrorMessage(errorMsg);
    } finally {
      // Remove from processing set
      setProcessingChanges(prev => {
        const updated = new Set(prev);
        updated.delete(changeKey);
        return updated;
      });
    }
  };

  // Generate week dates
  const weekDates = getWeekDates();

  // Render Weekly View
  return (
    <div className="employee-availability">
      <div className="employee-section-header">
        <h2>Çalışma Uygunluğu</h2>
        <p className="employee-instructions">
          Çalışabileceğiniz vardiyalar için ilgili günlerdeki onay kutucuklarını işaretleyin.
        </p>
      </div>

      {!currentUser && (
        <div className="employee-error-message">
          Kullanıcı bilgileri bulunamadı. Lütfen
          <a href="/employee/login" className="employee-login-link">
            giriş yapın
          </a>
          ve tekrar deneyin.
        </div>
      )}

      <div className="employee-calendar-controls">
        <div className="employee-week-navigation">
          <button 
            onClick={getPreviousWeek} 
            title="Önceki Hafta"
            className="employee-nav-button"
            disabled={loading}
          >
            <span className="employee-icon">«</span>
            <span className="employee-nav-text">Önceki Hafta</span>
          </button>
          <button 
            onClick={goToCurrentWeek} 
            className="employee-current-button"
            disabled={loading}
          >
            Bu Hafta
          </button>
          <button 
            onClick={getNextWeek} 
            title="Sonraki Hafta"
            className="employee-nav-button"
            disabled={loading}
          >
            <span className="employee-nav-text">Sonraki Hafta</span>
            <span className="employee-icon">»</span>
          </button>
        </div>
        <div className="employee-week-display">
          <h3>
            {new Date(currentWeekStart).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            {' - '}
            {new Date(new Date(currentWeekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </h3>
        </div>
      </div>
      
      {error && (
        <div className="employee-error-message">{error}</div>
      )}
      
      {loading ? (
        <LoadingIndicator message="Çalışma uygunlukları yükleniyor..." />
      ) : (
        <div className="employee-availability-table-container">
          <table className="employee-availability-table">
            <thead>
              <tr>
                <th className="employee-shift-type-header">Vardiya Türü</th>
                {weekDates.map(day => (
                  <th key={day.date} className="employee-day-header">
                    <div className="employee-day-name">{day.dayName}</div>
                    <div className="employee-day-date">{day.displayDate}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shiftTypes.map(shiftType => (
                <tr key={shiftType.id} className="employee-shift-type-row">
                  <td className="employee-shift-type-cell" style={{ borderLeft: `4px solid ${shiftType.color}` }}>
                    <div className="employee-shift-type-name">{shiftType.name}</div>
                    <div className="employee-shift-type-time">
                      {minutesToTimeString(shiftType.defaultStartTime)} - {minutesToTimeString(shiftType.defaultEndTime)}
                    </div>
                  </td>
                  
                  {weekDates.map(day => {
                    const isAvailable = availabilities[day.date]?.[shiftType.id] !== undefined;
                    const status = isAvailable ? availabilities[day.date][shiftType.id].status : null;
                    const changeKey = `${day.date}-${shiftType.id}`;
                    const isProcessing = processingChanges.has(changeKey);
                    
                    // Check if the day is in the past
                    const isPastDay = new Date(day.date) < new Date(new Date().setHours(0,0,0,0));
                    
                    // Disable checkbox for past days or when processing
                    const isDisabled = isPastDay || isProcessing;
                    
                    return (
                      <td key={`${day.date}-${shiftType.id}`} className="employee-availability-cell">
                        <label className={`employee-availability-checkbox ${status ? `employee-status-${status}` : ''} ${isProcessing ? 'employee-processing' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isAvailable}
                            onChange={() => !isDisabled && toggleAvailability(day.date, shiftType.id)}
                            disabled={isDisabled}
                          />
                          <span className="employee-checkbox-custom">
                            {isProcessing && <span className="employee-processing-spinner"></span>}
                          </span>
                          {status && !isProcessing && (
                            <span className="employee-availability-status">
                              {status === 'approved' && 'Onaylandı'}
                              {status === 'pending' && 'Beklemede'}
                              {status === 'rejected' && 'Reddedildi'}
                            </span>
                          )}
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeeAvailability;